import { useState, useEffect } from 'react'
import api from '../services/api'

const ManageCategories = ({ isOpen, onClose, onCategoriesUpdated }) => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [editingCategory, setEditingCategory] = useState(null)
  const [newCategory, setNewCategory] = useState({ name: '', description: '' })
  const [showCreateForm, setShowCreateForm] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadCategories()
    }
  }, [isOpen])

  const loadCategories = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/StaticAssets/categories/details')
      setCategories(response.data || [])
    } catch (err) {
      console.error('Failed to load categories:', err)
      setError('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  const createCategory = async () => {
    if (!newCategory.name.trim()) {
      setError('Category name is required')
      return
    }

    try {
      setError(null)
      await api.post('/StaticAssets/categories', {
        name: newCategory.name.trim(),
        description: newCategory.description.trim() || null
      })
      
      setNewCategory({ name: '', description: '' })
      setShowCreateForm(false)
      await loadCategories()
      onCategoriesUpdated?.()
    } catch (err) {
      console.error('Failed to create category:', err)
      setError(err.response?.data?.detail || 'Failed to create category')
    }
  }

  const updateCategory = async (categoryName, updates) => {
    try {
      setError(null)
      await api.put(`/StaticAssets/categories/${encodeURIComponent(categoryName)}`, updates)
      setEditingCategory(null)
      await loadCategories()
      onCategoriesUpdated?.()
    } catch (err) {
      console.error('Failed to update category:', err)
      setError(err.response?.data?.detail || 'Failed to update category')
    }
  }

  const deleteCategory = async (categoryName) => {
    if (!window.confirm(`Are you sure you want to delete the "${categoryName}" category? This action cannot be undone.`)) {
      return
    }

    try {
      setError(null)
      await api.delete(`/StaticAssets/categories/${encodeURIComponent(categoryName)}`)
      await loadCategories()
      onCategoriesUpdated?.()
    } catch (err) {
      console.error('Failed to delete category:', err)
      setError(err.response?.data?.detail || 'Failed to delete category')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Manage Categories</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Create New Category */}
          <div className="mb-6">
            {!showCreateForm ? (
              <button
                onClick={() => setShowCreateForm(true)}
                className="btn-primary px-4 py-2 rounded-md"
              >
                Create New Category
              </button>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h3 className="font-medium text-gray-900 mb-3">Create New Category</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Category name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={newCategory.description}
                      onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Optional description"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={createCategory}
                      className="btn-primary px-4 py-2 rounded-md"
                    >
                      Create
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateForm(false)
                        setNewCategory({ name: '', description: '' })
                        setError(null)
                      }}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Categories List */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Existing Categories</h3>
            {loading ? (
              <div className="text-center py-4">
                <div className="text-gray-500">Loading categories...</div>
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No categories found. Create your first category above.
              </div>
            ) : (
              <div className="space-y-3">
                {categories.map((category) => (
                  <div key={category.name} className="border border-gray-200 rounded-lg p-4">
                    {editingCategory?.name === category.name ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={editingCategory.description || ''}
                            onChange={(e) => setEditingCategory(prev => ({ 
                              ...prev, 
                              description: e.target.value 
                            }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Category description"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateCategory(category.name, { 
                              description: editingCategory.description || null 
                            })}
                            className="btn-primary px-3 py-1 rounded text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingCategory(null)}
                            className="px-3 py-1 text-gray-600 border border-gray-300 rounded text-sm hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-medium text-gray-900">{category.name}</h4>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                              {category.assetCount} assets
                            </span>
                          </div>
                          {category.description && (
                            <p className="text-sm text-gray-600 mb-2">{category.description}</p>
                          )}
                          <p className="text-xs text-gray-500">
                            Created: {new Date(category.createdAt).toLocaleDateString()}
                            {category.updatedAt && (
                              <> • Updated: {new Date(category.updatedAt).toLocaleDateString()}</>
                            )}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => setEditingCategory({
                              name: category.name,
                              description: category.description || ''
                            })}
                            className="text-indigo-600 hover:text-indigo-800 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteCategory(category.name)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ManageCategories